import { Container, Graphics, Rectangle } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W, DESIGN_H } from '../app/theme';
import { viewport } from '../app/viewport';
import { Button, makeText, sceneBackground, avatarSprite, iconSprite } from '../ui/widgets';
import { TextField, type TextFieldOpts } from '../ui/TextField';
import { AvatarPickerModal } from '../ui/AvatarPickerModal';
import { enterFade } from './common';
import type { AppContext } from '../app/AppContext';
import { RoomName } from '../net/enums';
import { MessageTag } from '../net/messageTags';
import {
  encodeCheckParent,
  decodeCheckParent,
  encodeRegister,
  decodeRegisterResult,
} from '../net/codec/register';
import { toPersianDigits } from '../util/persian';

/**
 * Account creation (port of Register_code.cs). The public flow is two-step: a referral-code GATE
 * (CheckParent(27) — photo_3) that, on success, reveals the full registration form (Register(4)).
 * Failures arrive as the global SendMessage(0) dialog. parentId is supplied by the CheckParent reply.
 */
export class RegisterScene extends Scene {
  private gate = new Container();
  private form = new Container();

  private codeField!: TextField;
  private username!: TextField;
  private password!: TextField;
  private email!: TextField;
  private emailRepeat!: TextField;
  private mobile!: TextField;

  private parentId = -1n;
  private avatarIndex = 0;
  private avatarHolder = new Container();
  private avatarModal?: AvatarPickerModal;

  constructor(private readonly ctx: AppContext) {
    super();
  }

  onEnter(): void {
    this.addChild(sceneBackground());
    this.buildGate();
    this.buildForm();
    this.form.visible = false;
    this.addChild(this.gate, this.form);
    enterFade([this.gate]);

    this.track(
      this.ctx.client.on(MessageTag.CheckParent, (r) => {
        this.parentId = decodeCheckParent(r);
        this.gate.visible = false;
        this.form.visible = true;
        enterFade([this.form]);
      }),
    );
    this.track(
      this.ctx.client.on(MessageTag.Register, (r) => {
        if (decodeRegisterResult(r)) {
          this.ctx.dialog({ message: 'ثبت نام با موفقیت انجام شد.' });
          this.ctx.navigate(RoomName.Login);
        }
      }),
    );
  }

  // --- Phase 1: referral-code gate (photo_3) --------------------------------------------------------

  private buildGate(): void {
    const cardW = 600;
    const cardH = 430;
    const cardX = (DESIGN_W - cardW) / 2;
    // Center the card on taller screens (baseline 1280 unchanged: the extra height is split evenly).
    const cardY = 396 + Math.round(Math.max(0, viewport.h - DESIGN_H) / 2);

    // Crowned-ace emblem floating just above the card top edge.
    const logo = iconSprite(this.ctx.iconTexture('logo') ?? this.ctx.respackFrame('logo'), 150);
    logo.position.set(DESIGN_W / 2 - 75, cardY - 128);
    this.gate.addChild(logo);

    // Centred glass card (matches the login form panel) holding the referral-code step.
    this.gate.addChild(
      new Graphics().roundRect(cardX + 3, cardY + 6, cardW, cardH, 28).fill({ color: COLORS.scrim, alpha: 0.32 }),
      new Graphics().roundRect(cardX, cardY, cardW, cardH, 28).fill({ color: COLORS.panelSoft, alpha: 0.28 }),
      new Graphics().roundRect(cardX + 2, cardY + 2, cardW - 4, cardH * 0.32, 26).fill({ color: COLORS.white, alpha: 0.035 }),
      new Graphics().roundRect(cardX, cardY, cardW, cardH, 28).stroke({ color: COLORS.panelBorder, width: 2, alpha: 0.55 }),
    );

    const title = makeText('ثبت نام', { size: 34, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(DESIGN_W / 2, cardY + 28);
    title.style.dropShadow = { color: 0x2a1c06, alpha: 0.6, blur: 0, distance: 2, angle: Math.PI / 2 };
    this.gate.addChild(title);
    this.gate.addChild(new Graphics().roundRect(DESIGN_W / 2 - 66, cardY + 76, 132, 2, 1).fill({ color: COLORS.accentDark, alpha: 0.9 }));

    const note = makeText('برای ثبت نام در بازی، کد معرف خود را وارد نمایید.', { size: 21, align: 'center', fill: COLORS.text });
    note.anchor.set(0.5, 0);
    note.style.wordWrap = true;
    note.style.wordWrapWidth = cardW - 96;
    note.position.set(DESIGN_W / 2, cardY + 104);
    this.gate.addChild(note);

    const fieldW = 520;
    const fieldX = (DESIGN_W - fieldW) / 2;
    this.codeField = new TextField({
      width: fieldW,
      height: 74,
      placeholder: 'کد معرف',
      align: 'right',
      icon: this.ctx.iconTexture('ticket'),
      enterKeyHint: 'go',
    });
    this.codeField.position.set(fieldX, cardY + 178);
    this.gate.addChild(this.codeField);

    const halfW = (fieldW - 20) / 2;
    const checkParent = (): void => this.ctx.client.send(encodeCheckParent(this.codeField.value.trim()));
    this.codeField.onKeyboardEnter(checkParent);
    const check = new Button('بررسی', checkParent, {
      width: halfW, height: 74, fontSize: 26, icon: this.ctx.iconTexture('enter'),
    });
    check.position.set(fieldX + halfW + 20, cardY + 286);
    this.gate.addChild(check);
    const back = new Button('بازگشت', () => this.ctx.navigate(RoomName.Login), {
      width: halfW, height: 74, fontSize: 26, fill: COLORS.panel, textColor: COLORS.text,
    });
    back.position.set(fieldX, cardY + 286);
    this.gate.addChild(back);

    const tag = makeText('با کد معرفِ دوستانت به بازی بپیوند', { size: 18, align: 'center', fill: COLORS.textDim });
    tag.anchor.set(0.5, 0);
    tag.position.set(DESIGN_W / 2, cardY + cardH + 40);
    this.gate.addChild(tag);
  }

  // --- Phase 2: registration form -------------------------------------------------------------------

  private buildForm(): void {
    const fieldW = 540;
    const fieldX = (DESIGN_W - fieldW) / 2;
    // The title stays at the top; the avatar/card/fields/buttons block drifts down by half the extra
    // height so the dense form stays vertically centered on tall screens (baseline 1280: dy = 0).
    const dy = Math.round(Math.max(0, viewport.h - DESIGN_H) / 2);

    const title = makeText('تکمیل اطلاعات', { size: 30, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(DESIGN_W / 2, 36);
    title.style.dropShadow = { color: 0x2a1c06, alpha: 0.6, blur: 0, distance: 2, angle: Math.PI / 2 };
    this.form.addChild(title);
    this.form.addChild(new Graphics().roundRect(DESIGN_W / 2 - 66, 80, 132, 2, 1).fill({ color: COLORS.accentDark, alpha: 0.9 }));

    // Avatar preview — tap to open the thumbnail-grid picker.
    this.avatarHolder.position.set(DESIGN_W / 2 - 48, 106 + dy);
    this.avatarHolder.eventMode = 'static';
    this.avatarHolder.cursor = 'pointer';
    this.avatarHolder.hitArea = new Rectangle(0, 0, 96, 142);
    this.avatarHolder.on('pointertap', () => this.openAvatarPicker());
    this.form.addChild(this.avatarHolder);
    this.refreshAvatar();

    // Glass card grounding the five fields (same translucent wood-on-show treatment as login).
    const cardX = 44;
    const cardY = 272 + dy;
    const cardW = DESIGN_W - cardX * 2;
    const cardH = 600;
    this.form.addChild(
      new Graphics().roundRect(cardX, cardY, cardW, cardH, 30).fill({ color: COLORS.panelSoft, alpha: 0.18 }),
      new Graphics().roundRect(cardX + 2, cardY + 2, cardW - 4, cardH * 0.3, 28).fill({ color: COLORS.white, alpha: 0.03 }),
      new Graphics().roundRect(cardX, cardY, cardW, cardH, 30).stroke({ color: COLORS.panelBorder, width: 2, alpha: 0.55 }),
    );

    const field = (
      placeholder: string,
      y: number,
      icon: ReturnType<AppContext['iconTexture']>,
      secure = false,
      opts: Partial<TextFieldOpts> = {},
    ): TextField => {
      const f = new TextField({ width: fieldW, height: 72, placeholder, align: 'right', secure, icon, ...opts });
      f.position.set(fieldX, y + dy);
      this.form.addChild(f);
      return f;
    };
    this.username = field('نام کاربری', 300, this.ctx.iconTexture('user'), false, { autocomplete: 'username', enterKeyHint: 'next' });
    this.password = field('رمز عبور', 412, this.ctx.iconTexture('lock'), true, { autocomplete: 'new-password', enterKeyHint: 'next' });
    this.email = field('ایمیل', 524, this.ctx.iconTexture('email'), false, { autocomplete: 'email', inputMode: 'email', enterKeyHint: 'next' });
    this.emailRepeat = field('تکرار ایمیل', 636, this.ctx.iconTexture('email'), false, { autocomplete: 'email', inputMode: 'email', enterKeyHint: 'next' });
    this.mobile = field('شماره موبایل', 748, this.ctx.iconTexture('phone'), false, { autocomplete: 'tel', inputMode: 'tel', enterKeyHint: 'done' });

    this.username.onKeyboardEnter(() => this.password.focus());
    this.password.onKeyboardEnter(() => this.email.focus());
    this.email.onKeyboardEnter(() => this.emailRepeat.focus());
    this.emailRepeat.onKeyboardEnter(() => this.mobile.focus());
    this.mobile.onKeyboardEnter(() => this.submit());

    const submit = new Button('ثبت نام', () => this.submit(), { width: fieldW, height: 84, fontSize: 28, icon: this.ctx.iconTexture('enter') });
    submit.position.set(fieldX, 912 + dy);
    this.form.addChild(submit);
    const back = new Button('بازگشت', () => this.ctx.navigate(RoomName.Login), {
      width: fieldW, height: 64, fontSize: 24, fill: COLORS.panel, textColor: COLORS.text,
    });
    back.position.set(fieldX, 1016 + dy);
    this.form.addChild(back);

    const tag = makeText('اطلاعات شما نزد ما محفوظ است', { size: 17, align: 'center', fill: COLORS.textDim });
    tag.anchor.set(0.5, 0);
    tag.position.set(DESIGN_W / 2, 1106 + dy);
    this.form.addChild(tag);
  }

  private refreshAvatar(): void {
    this.avatarHolder.removeChildren();
    this.avatarHolder.addChild(avatarSprite(this.ctx.profileAtlas, this.avatarIndex, 96));
    const lbl = makeText(`آواتار ${toPersianDigits(this.avatarIndex)}`, { size: 16, fill: COLORS.textDim, align: 'center' });
    lbl.anchor.set(0.5, 0);
    lbl.position.set(48, 100);
    this.avatarHolder.addChild(lbl);
    const hint = makeText('تغییر آواتار', { size: 15, weight: '700', fill: COLORS.gold, align: 'center' });
    hint.anchor.set(0.5, 0);
    hint.position.set(48, 122);
    this.avatarHolder.addChild(hint);
  }

  private openAvatarPicker(): void {
    if (this.avatarModal) return;
    const modal = new AvatarPickerModal(
      this.ctx.profileAtlas,
      this.avatarIndex,
      (idx) => {
        this.avatarIndex = idx;
        this.refreshAvatar();
        this.closeAvatarPicker();
      },
      () => this.closeAvatarPicker(),
    );
    this.avatarModal = modal;
    this.addChild(modal);
  }

  private closeAvatarPicker(): void {
    this.avatarModal?.destroy({ children: true });
    this.avatarModal = undefined;
  }

  private submit(): void {
    if (this.email.value.trim() !== this.emailRepeat.value.trim()) {
      this.ctx.dialog({ message: 'ایمیل وارد شده یکسان نیست.' });
      return;
    }
    this.ctx.client.send(
      encodeRegister({
        username: this.username.value.trim(),
        password: this.password.value,
        email: this.email.value.trim(),
        mobile: this.mobile.value.trim(),
        parentId: this.parentId,
        avatarId: this.avatarIndex,
      }),
    );
  }
}
